import { useCallback, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { reconReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmt, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import { Pill, CountChip, Pager, Dash, errMessage, DEFAULT_PAGE_SIZE } from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type { ControlExceptionsDto, ControlExceptionsFilter, ControlExceptionRow } from '@/types/reportCentre.types';

const REPORT_KEY = 'CONTROL_EXCEPTIONS';

/**
 * Manual postings and unexpected sources are direct control breaches — red.
 * Missing references and inactive-account posts are hygiene issues — amber.
 */
const EXCEPTION_TONES: Record<string, PillTone> = {
  MANUAL_POSTING_TO_CONTROL: 'red',
  UNEXPECTED_SOURCE_MODULE:  'red',
  MISSING_SOURCE_REFERENCE:  'amber',
  INACTIVE_ACCOUNT_POSTING:  'amber',
};

const EXCEPTION_LABELS: Record<string, string> = {
  MANUAL_POSTING_TO_CONTROL: 'Manual Posting to Control',
  UNEXPECTED_SOURCE_MODULE:  'Unexpected Source Module',
  MISSING_SOURCE_REFERENCE:  'Missing Source Reference',
  INACTIVE_ACCOUNT_POSTING:  'Inactive Account Posting',
};

// ── Export ────────────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Account',    header: 'Control Account',  width: 30 },
  { key: 'Journal',    header: 'Journal No',       width: 18 },
  { key: 'Date',       header: 'Posting Date',     width: 14 },
  { key: 'SourceMod',  header: 'Source Module',    width: 16 },
  { key: 'SourceDoc',  header: 'Source Doc',       width: 18 },
  { key: 'Expected',   header: 'Expected Source',  width: 18 },
  { key: 'Actual',     header: 'Actual Source',    width: 18 },
  { key: 'User',       header: 'Created By',       width: 20 },
  { key: 'Debit',      header: 'Debit',            width: 14, numFmt: '#,##0.00' },
  { key: 'Credit',     header: 'Credit',           width: 14, numFmt: '#,##0.00' },
  { key: 'Type',       header: 'Exception Type',   width: 26 },
];

function buildExportRows(rows: ControlExceptionRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Account:   `${r.controlAccountCode} — ${r.controlAccountName}`,
    Journal:   r.journalNumber,
    Date:      r.postingDate,
    SourceMod: r.sourceModule ?? '',
    SourceDoc: r.sourceDocumentNo ?? '',
    Expected:  r.expectedSource ?? '',
    Actual:    r.actualSource ?? '',
    User:      r.createdByName ?? '',
    Debit:     r.debit,
    Credit:    r.credit,
    Type:      EXCEPTION_LABELS[r.exceptionType] ?? r.exceptionType,
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ControlExceptionsPage = () => {
  const { hasPermission } = usePermission();

  const [filter, setFilter] = useState<ControlExceptionsFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [applied, setApplied] = useState<ControlExceptionsFilter | null>(null);
  const [data, setData] = useState<ControlExceptionsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (base: ControlExceptionsFilter, pageNumber: number) => {
    const query = { ...base, pageNumber };
    setLoading(true);
    setError(null);
    try {
      const res = await reconReportApi.getControlExceptions(query);
      setData(res.data?.data ?? null);
      setApplied(query);
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const run = useCallback(() => { void fetchPage(filter, 1); }, [fetchPage, filter]);

  if (!hasPermission(Permissions.RPT_RECON)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const page = data?.page;
  const items = page?.items ?? [];

  const handleExcel = () => {
    if (!data || !page) return;
    exportToExcel({
      sheetName: 'Control Exceptions',
      columns: EXCEL_COLUMNS,
      rows: buildExportRows(items),
      title: 'Control Account Exceptions',
      subtitle: `${data.companyName} · ${data.fromDate} to ${data.toDate} · Page ${page.pageNumber} of ${page.totalPages}`,
    }, `ControlExceptions_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  const handleCsv = () => {
    if (!data || !page) return;
    exportToCsvRows(EXCEL_COLUMNS, buildExportRows(items), `ControlExceptions_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldAlert size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Control Account Exceptions</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={!!data}
          reportKey={REPORT_KEY}
          rowCount={items.length}
          filterSummary={`${filter.fromDate}..${filter.toDate}`}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4')}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            <div className="w-44">
              <label className={LABEL_CLS}>From Date *</label>
              <input
                type="date"
                value={filter.fromDate}
                onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
                className={INPUT_CLS}
              />
            </div>
            <div className="w-44">
              <label className={LABEL_CLS}>To Date *</label>
              <input
                type="date"
                value={filter.toDate}
                onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
                className={INPUT_CLS}
              />
            </div>
          </div>
          <RunReportButton onClick={run} isLoading={loading} disabled={!filter.fromDate || !filter.toDate} />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
      )}

      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to scan control accounts for exceptions.' />
      )}

      {data && page && (
        <div className="flex flex-col gap-4">
          {/* Summary chips — whole window, not just this page */}
          <div className="flex flex-wrap items-center gap-2">
            {data.summary.length === 0 ? (
              <CountChip label="No exceptions in this window" count={0} tone="green" />
            ) : data.summary.map(s => (
              <CountChip
                key={s.exceptionType}
                label={EXCEPTION_LABELS[s.exceptionType] ?? s.exceptionType}
                count={s.count}
                tone={EXCEPTION_TONES[s.exceptionType] ?? 'grey'}
              />
            ))}
          </div>

          {/* Table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Exceptions</h2>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                {data.companyName} · {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              </span>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1400px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={TH_CLS}>Control Account</th>
                    <th className={TH_CLS}>Journal No</th>
                    <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                    <th className={TH_CLS}>Source Module</th>
                    <th className={TH_CLS}>Source Doc</th>
                    <th className={TH_CLS}>Expected Source</th>
                    <th className={TH_CLS}>Actual Source</th>
                    <th className={TH_CLS}>Created By</th>
                    <th className={TH_R_CLS}>Debit</th>
                    <th className={TH_R_CLS}>Credit</th>
                    <th className={cn(TH_CLS, 'w-52')}>Exception</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No control-account exceptions in the selected window.
                      </td>
                    </tr>
                  ) : items.map((r, i) => (
                    <tr key={`${r.journalNumber}-${r.controlAccountCode}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                        <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-1.5">{r.controlAccountCode}</span>
                        {r.controlAccountName}
                      </td>
                      <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.journalNumber}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.postingDate)}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.sourceModule ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400">{r.sourceDocumentNo ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.expectedSource ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.actualSource ?? <Dash />}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{r.createdByName ?? <Dash />}</td>
                      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.debit)}</td>
                      <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.credit)}</td>
                      <td className="px-3 py-2">
                        <Pill
                          label={EXCEPTION_LABELS[r.exceptionType] ?? r.exceptionType}
                          tone={EXCEPTION_TONES[r.exceptionType] ?? 'grey'}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              pageNumber={page.pageNumber}
              totalPages={page.totalPages}
              totalCount={page.totalCount}
              shownCount={items.length}
              loading={loading}
              onPage={p => { void fetchPage(applied ?? filter, p); }}
              noun="exceptions"
            />
          </div>
        </div>
      )}
    </div>
  );
};
